import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { FeatureFlag } from "@shared/schema";

export const defaultFlags = {
  walletEnabled: true,
  subscriptionsEnabled: true,
  paymentPlansEnabled: true,
  communityFundsEnabled: true,
  referralsEnabled: true,
  challengesEnabled: true,
  communityEnabled: true,
} as const;

export type FeatureFlagKey = keyof typeof defaultFlags;

export const featureFlags = { ...defaultFlags };

export function isFeatureEnabled(flag: FeatureFlagKey): boolean {
  return featureFlags[flag] ?? false;
}

export const disabledRoutes: string[] = [];

export const disabledSidebarUrls = new Set(disabledRoutes.map(r => r.split("/:")[0]));

function flagsArrayToMap(flags: FeatureFlag[]): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const f of flags) {
    map[f.key] = f.enabled;
  }
  return map;
}

function getResolvedFlags(apiFlags: FeatureFlag[] | undefined): typeof defaultFlags {
  if (!apiFlags || apiFlags.length === 0) return { ...defaultFlags };
  const map = flagsArrayToMap(apiFlags);
  const resolved = { ...defaultFlags };
  for (const key of Object.keys(defaultFlags) as FeatureFlagKey[]) {
    if (key in map) {
      (resolved as any)[key] = map[key];
    }
  }
  return resolved;
}

export function computeDisabledSidebarUrls(_flags: typeof defaultFlags): Set<string> {
  return new Set<string>();
}

export function useFeatureFlags() {
  const { data: apiFlags, isLoading } = useQuery<FeatureFlag[]>({
    queryKey: ["/api/feature-flags"],
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });

  const resolved = getResolvedFlags(apiFlags);
  const disabledUrls = computeDisabledSidebarUrls(resolved);

  return {
    flags: resolved,
    rawFlags: apiFlags || [],
    disabledSidebarUrls: disabledUrls,
    isLoading,
  };
}

export function useToggleFeatureFlag() {
  return useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) => {
      return apiRequest("PATCH", `/api/admin/feature-flags/${key}`, { enabled });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/feature-flags"] });
    },
  });
}
